import {Component} from '@wonderlandengine/api';
import {quat, vec3} from 'gl-matrix';
import {Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';

const VRM_AIM_AXES: Record<string, [number, number, number]> = {
    PositiveX: [1.0, 0.0, 0.0],
    NegativeX: [-1.0, 0.0, 0.0],
    PositiveY: [0.0, 1.0, 0.0],
    NegativeY: [0.0, -1.0, 0.0],
    PositiveZ: [0.0, 0.0, 1.0],
    NegativeZ: [0.0, 0.0, -1.0],
};

const Rad2Deg = 180.0 / Math.PI;
const RightVector = vec3.fromValues(1, 0, 0);
const UpVector = vec3.fromValues(0, 1, 0);
const ForwardVector = vec3.fromValues(0, 0, 1);

interface HumanoidBones {
    [key: string]: Object3D | null;
    /* Torso */
    hips: Object3D | null;
    spine: Object3D | null;
    chest: Object3D | null;
    neck: Object3D | null;
    head: Object3D | null;

    /* Legs */
    leftUpperLeg: Object3D | null;
    leftLowerLeg: Object3D | null;
    leftFoot: Object3D | null;
    rightUpperLeg: Object3D | null;
    rightLowerLeg: Object3D | null;
    rightFoot: Object3D | null;

    /* Arms */
    leftUpperArm: Object3D | null;
    leftLowerArm: Object3D | null;
    leftHand: Object3D | null;
    rightUpperArm: Object3D | null;
    rightLowerArm: Object3D | null;
    rightHand: Object3D | null;

    leftShoulder: Object3D | null;
    rightShoulder: Object3D | null;
    leftToes: Object3D | null;
    rightToes: Object3D | null;
    leftEye: Object3D | null;
    rightEye: Object3D | null;
    leftThumbProximal: Object3D | null;
    leftThumbIntermediate: Object3D | null;
    leftThumbDistal: Object3D | null;
    leftIndexProximal: Object3D | null;
    leftIndexIntermediate: Object3D | null;
    leftIndexDistal: Object3D | null;
    leftMiddleProximal: Object3D | null;
    leftMiddleIntermediate: Object3D | null;
    leftMiddleDistal: Object3D | null;
    leftRingProximal: Object3D | null;
    leftRingIntermediate: Object3D | null;
    leftRingDistal: Object3D | null;
    leftLittleProximal: Object3D | null;
    leftLittleIntermediate: Object3D | null;
    leftLittleDistal: Object3D | null;
    rightThumbProximal: Object3D | null;
    rightThumbIntermediate: Object3D | null;
    rightThumbDistal: Object3D | null;
    rightIndexProximal: Object3D | null;
    rightIndexIntermediate: Object3D | null;
    rightIndexDistal: Object3D | null;
    rightMiddleProximal: Object3D | null;
    rightMiddleIntermediate: Object3D | null;
    rightMiddleDistal: Object3D | null;
    rightRingProximal: Object3D | null;
    rightRingIntermediate: Object3D | null;
    rightRingDistal: Object3D | null;
    rightLittleProximal: Object3D | null;
    rightLittleIntermediate: Object3D | null;
    rightLittleDistal: Object3D | null;
    upperChest: Object3D | null;
}

interface LookAt {
    lookAtHorizontalInner: number;
    lookAtHorizontalOuter: number;
    lookAtVerticalDown: number;
    lookAtVerticalUp: number;
}

/**
 * Component for loading and handling VRM 0.x models.
 */
export class Vrm0 extends Component {
    static TypeName = 'vrm';

    /** URL to a VRM file to load */
    @property.string()
    src!: string;

    /** Object the VRM is looking at */
    @property.object()
    lookAtTarget!: Object3D | null;

    /** Meta information about the VRM model */
    meta: any = null;

    /* Contains details for (bone type) lookAt behaviour */
    private _lookAt: LookAt | null = null;

    /** The humanoid bones of the VRM model */
    bones: HumanoidBones = {
        /* Torso */
        hips: null,
        spine: null,
        chest: null,
        neck: null,
        head: null,

        /* Legs */
        leftUpperLeg: null,
        leftLowerLeg: null,
        leftFoot: null,
        rightUpperLeg: null,
        rightLowerLeg: null,
        rightFoot: null,

        /* Arms */
        leftUpperArm: null,
        leftLowerArm: null,
        leftHand: null,
        rightUpperArm: null,
        rightLowerArm: null,
        rightHand: null,

        leftShoulder: null,
        rightShoulder: null,
        leftToes: null,
        rightToes: null,
        leftEye: null,
        rightEye: null,
        leftThumbProximal: null,
        leftThumbIntermediate: null,
        leftThumbDistal: null,
        leftIndexProximal: null,
        leftIndexIntermediate: null,
        leftIndexDistal: null,
        leftMiddleProximal: null,
        leftMiddleIntermediate: null,
        leftMiddleDistal: null,
        leftRingProximal: null,
        leftRingIntermediate: null,
        leftRingDistal: null,
        leftLittleProximal: null,
        leftLittleIntermediate: null,
        leftLittleDistal: null,
        rightThumbProximal: null,
        rightThumbIntermediate: null,
        rightThumbDistal: null,
        rightIndexProximal: null,
        rightIndexIntermediate: null,
        rightIndexDistal: null,
        rightMiddleProximal: null,
        rightMiddleIntermediate: null,
        rightMiddleDistal: null,
        rightRingProximal: null,
        rightRingIntermediate: null,
        rightRingDistal: null,
        rightLittleProximal: null,
        rightLittleIntermediate: null,
        rightLittleDistal: null,
        upperChest: null,
    };

    /** Rotations of the bones in the rest pose (T-pose) */
    restPose: {[key: string]: quat} = {};

    private _tempV3 = vec3.create();
    private _tempV3A = vec3.create();
    private _tempV3B = vec3.create();
    private _tempQuat = quat.create();
    private _tempQuatA = quat.create();

    /* Whether or not the VRM component has been initialized */
    private _initialized: boolean = false;

    async start() {
        if (!this.src) {
            console.error('vrm0: src property not set');
            return;
        }

        const prefab = await this.engine.loadGLTF({url: this.src, extensions: true});

        const {root} = this.engine.scene.instantiate(prefab);
        root.parent = this.object;
        this._initializeVrm(prefab);
    }

    /**
     * Parses the VRM glTF extensions and initializes the vrm component.
     */
    private _initializeVrm(prefab: any): void {
        if (this._initialized) {
            throw new Error('VRM component has already been initialized');
        }

        let vrm = prefab.extensions.root.VRM;

        if (!vrm) {
            throw new Error('Missing VRM extensions');
        }

        this.meta = vrm.meta;
        this._parseHumanoid(vrm.humanoid, prefab);

        if (vrm.lookAt) {
            this._parseLookAt(vrm.lookAt);
        }

        this._initialized = true;
    }

    private _parseHumanoid(humanoid: any, prefab: any): void {
        let text = '';
        for (const {bone, node, useDefaultValues} of humanoid.humanBones) {
            if (!(bone in this.bones)) {
                console.warn(`[vrm 0.x] Unsupported bone '${bone}'`);
                continue;
            }

            this.bones[bone] = prefab.extensions.node[node];
            if (this.bones[bone]) {
                this.restPose[bone] = this.bones[bone]!.getRotationLocal(quat.create());
            }
        }
    }

    private _parseLookAt(lookAt: any): void {
        this._lookAt = {
            lookAtHorizontalInner: lookAt.lookAtHorizontalInner,
            lookAtHorizontalOuter: lookAt.lookAtHorizontalOuter,
            lookAtVerticalDown: lookAt.lookAtVerticalDown,
            lookAtVerticalUp: lookAt.lookAtVerticalUp,
        };
    }

    update(dt: number) {
        if (!this._initialized) {
            return;
        }

        this._resolveLookAt();
    }

    private _resolveLookAt(): void {
        if (!this._lookAt || !this.lookAtTarget || !this.bones.head) {
            return;
        }

        /* 1. calculate direction from head to target. */
        const lookAtPosition = this.lookAtTarget.getTranslationWorld(this._tempV3);
        const headPosition = this.bones.head.getTranslationWorld(this._tempV3A);
        const lookAtDirection = vec3.subtract(this._tempV3B, lookAtPosition, headPosition);
        vec3.normalize(lookAtDirection, lookAtDirection);

        this.bones.head.transformVectorInverseWorld(lookAtDirection);

        let x = vec3.dot(lookAtDirection, RightVector);
        let y = vec3.dot(lookAtDirection, UpVector);
        let z = vec3.dot(lookAtDirection, ForwardVector);

        const yaw = Math.atan2(x, z) * Rad2Deg;
        const pitch = Math.atan2(y, Math.sqrt(x * x + z * z)) * Rad2Deg;

        /* Orient head towards target */
        const headRotation = quat.fromEuler(this._tempQuat, pitch, yaw, 0);

        if (this.bones.head && this.restPose.head) {
            this.bones.head.setRotationLocal(
                quat.multiply(this._tempQuat, headRotation, this.restPose.head)
            );
        }
    }
}
